// libraries
import React, { useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { startCase, toLower } from 'lodash'
import Dinero from 'dinero.js'
import Big from 'big.js'
import { v4 as uuidv4 } from 'uuid'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Stack, Grid, Typography, Button, Tooltip, CircularProgress } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import LockIcon from '@mui/icons-material/Lock'
import { useForm, FormProvider } from 'react-hook-form'
import { useParams, useNavigate } from 'react-router-dom'
import { shallowEqual, useDispatch } from 'react-redux'
import { request } from 'customRequest'
import printJS from 'print-js'

// queries and mutations
import {
  useGetBatchBillQuery,
  useCreateBatchBillMutation,
  useUpdateBatchBillMutation,
  usePayBatchBillMutation,
} from 'services/batchBills'
import { useGetInboundTicketsByIdsMutation } from 'services/inboundTickets'
import { useUpdatePaymentMutation } from 'services/payments'

// components
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import TabsLayoutContext from 'components/layouts/TabsLayoutContextProvider'
import SubFormLayout from 'components/layouts/SubFormLayout'
import BatchBillFooter from 'components/containers/batch-bills/BatchBillFooter'
import BatchBillTicketTable from 'components/containers/batch-bills/BatchBillTicketTable'
import InboundTicketsModal from 'components/generic/modals/dialog-modal/InboundTicketsModal'
import { Chip } from 'components/generic/chip'
import PrintCheckBlade from 'components/containers/bills/components/PrintCheckBlade'

// helpers and utils
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import useGetPricePrepend from 'utils/custom-hooks/useGetPricePrepend'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import useQzTray from 'utils/custom-hooks/useQzTray'
import { formOptions } from 'utils/validation/batchBills'
import {
  ALL_PAYMENT_METHOD_OPTIONS,
  CURRENCY_OPTIONS,
  BILL_STATUSES,
  TICKET_STATUSES,
  ROUNDING_PRECISIONS,
  PDF_TYPE,
  PAYMENT_METHODS_VALUES,
} from 'utils/constants'
import { ACTION_TYPE } from 'components/generic/buttons/ButtonList'
import {
  setDecimalPrecision,
  checkIfDateIsLocked,
  generateLockTooltip,
  dispatchSnackbar,
  isPrintableCheckPayment,
} from 'utils/helpers'
import { addToDate } from 'utils/data-manipulation/common'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'
import { useRefetchBatchBill } from 'components/containers/batch-bills/useRefetchBatchBill'
import { incrementCheckNumber } from 'components/containers/inbound-tickets/utils'

const BatchBillContainer = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { qzPrint } = useQzTray()

  const contentContext = useContext(ContentLayoutContext)
  const tabsContext = useContext(TabsLayoutContext)

  const idempotencyKey = useRef(uuidv4())

  useEffect(() => {
    tabsContext.setContainerTabsVisibility(false)
  }, [])

  const { id: batchBillId } = useParams()
  const isUpdate = !!batchBillId

  const methods = useForm(formOptions)
  const { handleSubmit, watch, reset, formState } = methods
  const { dirtyFields } = formState
  const isDirty = !!Object.keys(dirtyFields).length

  const {
    data: batchBillData,
    error: batchBillError,
    isFetching: isFetchingBatchBill,
    refetch,
  } = useGetBatchBillQuery({ batchBillId }, { skip: !batchBillId })

  const batchBillIsPaid = useMemo(() => batchBillData?.status === BILL_STATUSES.PAID.id, [batchBillData])

  const [getInboundTicketsByIds, { isLoading: isLoadingInboundTicketsByIds }] = useGetInboundTicketsByIdsMutation()
  const [createBatchBill, { isLoading: isCreatingBatchBill }] = useCreateBatchBillMutation()
  const [updateBatchBill, { isLoading: isUpdatingBatchBill }] = useUpdateBatchBillMutation()
  const [payBatchBill, { isLoading: isPayingBatchBill }] = usePayBatchBillMutation()
  const [updatePayment, { isLoading: isUpdatingPayment }] = useUpdatePaymentMutation()

  // redirect back to list view if batch bill is not found (likely attempting to view a batch bill for a different yard)
  useEffect(() => {
    if (batchBillError && 'status' in batchBillError && batchBillError.status === 404) {
      navigate('/app/purchases/batch-bills')
    }
  }, [batchBillError, navigate])

  const [afterSaveCallback, setAfterSaveCallback] = useState<(() => void) | null>(null)

  const [aggregatedData, setAggregatedData] = useState({})
  const [isFetchingPdf, setIsFetchingPdf] = useState(false)
  const [pageIsReady, setPageIsReady] = useState(!isUpdate)
  const [hasEditedInboundTickets, setHasEditedInboundTickets] = useState(false)
  const [billTotalValues, setBillTotalValues] = useState<{ [key: string]: number }>({})
  const [openAddModal, setOpenAddModal] = useState(false)
  const [groupedTicketsData, setGroupedTicketsData] = useState({})
  const [groupedTicketsArray, setGroupedTicketsArray] = useState<any[]>([])
  const groupedTicketsArrayRef = useRef<any[]>([])
  const [hasLockedTickets, setHasLockedTickets] = useState(false)
  const [openPrintCheckBlade, setOpenPrintCheckBlade] = useState(false)
  const [paymentsData, setPaymentsData] = useState([])
  const [loadingPaymentsData, setLoadingPaymentsData] = useState(false)

  const unprintedCheckPaymentsData = useMemo(() => {
    if (
      !paymentsData ||
      !batchBillIsPaid ||
      batchBillData?.paymentMethod !== PAYMENT_METHODS_VALUES.CHECK.toLowerCase()
    )
      return []
    return groupedTicketsArray
      .map((groupedTickets: any[]) => {
        const payment = paymentsData?.find(
          (payment: any) => payment.bill.customerId === groupedTickets?.[0]?.customerId,
        )
        return payment
      })
      .filter((payment: any) => !!payment && !payment.accountId)
  }, [paymentsData, groupedTicketsArray, batchBillData, batchBillIsPaid])

  const { validationSchema, customersDataArray, selectedTicketsDataArray, startingCheckNumberOverrides } =
    useMemo(() => {
      if (
        !batchBillData ||
        !batchBillIsPaid ||
        unprintedCheckPaymentsData.length === 0 ||
        Object.keys(groupedTicketsData).length === 0
      ) {
        return {
          validationSchema: {},
          customersDataArray: [],
          selectedTicketsDataArray: [],
          startingCheckNumberOverrides: [],
        }
      }

      const newValidationSchema = {}
      unprintedCheckPaymentsData.forEach((payment: any) => {
        newValidationSchema[payment.id] = Yup.object().shape({
          details: Yup.object().shape({
            payFrom: Yup.string().required('Pay From is Required'),
            checkNumber: Yup.string().required('Check Number is Required'),
          }),
        })
      })

      return {
        validationSchema: newValidationSchema,
        customersDataArray: unprintedCheckPaymentsData.map((payment: any) => {
          const customerId = payment.bill.customerId
          return aggregatedData?.[customerId]?.customerData
        }),
        selectedTicketsDataArray: unprintedCheckPaymentsData.map((payment: any) => {
          const customerId = payment.bill.customerId
          return groupedTicketsData?.[customerId]
        }),
        startingCheckNumberOverrides: unprintedCheckPaymentsData.map((payment: any) => payment.checkNumber),
      }
    }, [batchBillData, batchBillIsPaid, aggregatedData, groupedTicketsData, unprintedCheckPaymentsData])

  const {
    handleSubmit: checkPrintHandleSubmit,
    control: checkPrintControl,
    reset: checkPrintReset,
    getValues: checkPrintGetValues,
    setValue: checkPrintSetValue,
    watch: checkPrintWatch,
    trigger: checkPrintTrigger,
    formState: { errors: checkPrintErrors, isValid: checkPrintIsValid },
  } = useForm({ resolver: yupResolver(Yup.object().shape(validationSchema)) })

  const { pageIsSavingOrPaying, setPageIsSavingOrPaying } = useRefetchBatchBill({
    refetchFunction: refetch,
    isCreatingBatchBill,
    isUpdatingBatchBill,
    isPayingBatchBill,
    batchBillStatus: batchBillData?.status || '',
    afterSaveCallback,
  })

  const customerDataIsLoading = useMemo(() => {
    if (groupedTicketsArray.length === 0) {
      return false
    }
    return groupedTicketsArray.some((groupedTickets: any[]) => {
      const customerId = groupedTickets?.[0]?.customerId
      return !aggregatedData?.[customerId]?.customerData
    })
  }, [groupedTicketsArray, aggregatedData])

  const isLoading =
    isFetchingBatchBill ||
    !pageIsReady ||
    isLoadingInboundTicketsByIds ||
    pageIsSavingOrPaying ||
    customerDataIsLoading ||
    isFetchingPdf ||
    isUpdatingPayment ||
    loadingPaymentsData

  useGlobalLoader(isLoading)
  const showSkeletonLoaders = isUpdate && !batchBillData

  const { hasAtm, yardHasGsPayConfigured, yardPreferredPaymentMethod, lockAccountingPeriod, defaultCurrency } =
    useAppSelector(state => {
      const { hasAtm, details, settings, defaultCurrency } = state?.settings || {}
      return {
        hasAtm,
        yardHasGsPayConfigured: details?.gsPayEnabled,
        yardPreferredPaymentMethod: settings?.purchaseSettings?.payment?.yardPreferredPaymentMethod,
        lockAccountingPeriod: settings?.overviewSettings?.overview?.lockAccountingPeriod,
        defaultCurrency,
      }
    }, shallowEqual)

  const { enableLockAccountingV2 } = useAppSelector(state => state.featureFlags?.variables || {})

  const currency = watch('currency')
  const { pricePrepend } = useGetPricePrepend({ selectedCurrency: currency })

  const isInLockedAccountingPeriodV2 = useMemo(() => {
    if (!enableLockAccountingV2) return false
    if (!batchBillData || !lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return false
    if (hasLockedTickets) return true
    return checkIfDateIsLocked(lockAccountingPeriod, batchBillData.completionDate)
  }, [lockAccountingPeriod, batchBillData, enableLockAccountingV2, hasLockedTickets])

  const PAYMENT_METHOD_OPTIONS = useMemo(
    () =>
      ALL_PAYMENT_METHOD_OPTIONS?.filter(
        method => (hasAtm ? true : method.id !== 'atm') && (yardHasGsPayConfigured ? true : method.id !== 'gs_pay'),
      ),
    [hasAtm, yardHasGsPayConfigured],
  )

  const billIds = useMemo(() => {
    if (!batchBillData) return []
    return batchBillData?.bills?.map(bill => bill.id)
  }, [batchBillData])

  const onPrintCheck = async () => {
    try {
      if (unprintedCheckPaymentsData.length === 0) return
      const updatePaymentResponses = await (unprintedCheckPaymentsData as any).reduce(async (prevPromise, payment) => {
        const results = await prevPromise
        const paymentId = payment.id
        const checkingAccountId = checkPrintGetValues(`${paymentId}.details.payFrom`)
        const sanitizedCheckingAccountId = checkingAccountId === 'Main Checking Account' ? undefined : checkingAccountId
        const bill = aggregatedData[payment.bill?.customerId]?.billData

        const formattedData = {
          id: paymentId,
          billId: bill?.id,
          paymentDate: batchBillData?.paymentDate ? new Date(batchBillData.paymentDate).getTime() : null,
          completionDate: batchBillData?.completionDate ? new Date(batchBillData.completionDate).getTime() : null,
          paymentMethod: batchBillData?.paymentMethod,
          accountId: sanitizedCheckingAccountId,
          payToContactId: checkPrintGetValues(`${paymentId}.information.payToTheOrder`)?.id,
          payToLocationId: checkPrintGetValues(`${paymentId}.information.location`)?.id,
          total: {
            amount: (bill?.total || 0) * 100,
            currency: batchBillData?.currency,
            precision: 2,
          },
          inboundTickets: bill.inboundTicketIds,
          checkNumber: checkPrintGetValues(`${paymentId}.details.checkNumber`),
          isManualCheckNumber: true,
          checkMemo: checkPrintGetValues(`${paymentId}.information.memo`),
        }
        const response = await updatePayment({ paymentId, payment: formattedData })
        return [...results, response]
      }, Promise.resolve([]))
      const updatePaymentError = (updatePaymentResponses as any).find(response => !!response.error)?.error
      if (updatePaymentError) throw new Error(updatePaymentError?.data?.error)
    } catch (error) {
      const message = 'An error occurred updating the payment'
      dispatchSnackbar(dispatch, 'error', message)
    }
  }

  const pdfPrint = (billData, pdfUrl, pdfTitle = '', documentType = PDF_TYPE.BILL_SETTLEMENT_DOC) => {
    setIsFetchingPdf(true)
    request(pdfUrl, 'get', {}, { accept: 'application/pdf' }, true)
      .then(async response => {
        // Get appropriate printer based on document type
        const printerKeyMap = {
          [PDF_TYPE.BILL_SETTLEMENT_DOC]: 'documentPrinter',
          [PDF_TYPE.CHECK]: 'checkPrinter',
        }
        const selectedPrinter = localStorage.getItem(printerKeyMap[documentType])

        if (!selectedPrinter || selectedPrinter === 'none') {
          // Use default printJS if no printer is selected
          printJS({
            printable: response,
            type: 'pdf',
            base64: true,
            documentTitle: pdfTitle || `Payment #${billData?.externalId}`,
          })
        } else {
          try {
            if (documentType === PDF_TYPE.CHECK) {
              await qzPrint(response, selectedPrinter, 1) // Pass explicit copies=1
            } else {
              await qzPrint(response, selectedPrinter)
            }
          } catch (error) {
            // Fallback to printJS if QZ fails
            printJS({
              printable: response,
              type: 'pdf',
              base64: true,
              documentTitle: pdfTitle || `Payment #${billData?.externalId}`,
            })
          }
        }
      })
      .catch(error => {
        dispatchSnackbar(dispatch, 'error', 'An error occurred while generating the PDF document')
      })
      .finally(() => {
        setIsFetchingPdf(false)
      })
  }

  const fetchPayments = async paymentIds => {
    await paymentIds
      .reduce(async (prevPromise, paymentId) => {
        const results = await prevPromise
        const response = await request(`/core-api/purchases/payments/${paymentId}`)
        return [...results, response]
      }, Promise.resolve([]))
      .then(results => {
        const filteredResults = results.filter(payment => payment.status !== 'VOID')

        setLoadingPaymentsData(false)
        setPaymentsData(filteredResults)
      })
      .catch(error => {
        setLoadingPaymentsData(false)
        const message = error?.data?.error || 'An error occurred loading the payments'
        dispatchSnackbar(dispatch, 'error', message)
      })
  }

  const fetchTickets = useCallback(
    async inboundTicketIds => {
      try {
        const groupedTickets = {}
        let someTicketsAreLocked

        if (inboundTicketIds?.length > 0) {
          const { data: selectedInboundTicketsData, error: getSelectedInboundTicketsError } =
            await getInboundTicketsByIds({
              body: inboundTicketIds,
            })
          if (getSelectedInboundTicketsError) throw new Error(getSelectedInboundTicketsError?.data?.error)

          selectedInboundTicketsData?.Items?.forEach(ticket => {
            let feeOrPromotionAmount = 0
            if (ticket?.status?.value === TICKET_STATUSES.PAID.id) {
              feeOrPromotionAmount = ticket.feeOrPromotion?.reduce((acc, feeOrPromotion) => {
                const amountToAdd = feeOrPromotion.applied ? feeOrPromotion.calculatedValue : 0
                return (acc += amountToAdd)
              }, 0)
            } else {
              feeOrPromotionAmount = ticket.feeOrPromotion?.reduce((acc, feeOrPromotion) => {
                return (acc += feeOrPromotion?.calculatedValue || 0)
              }, 0)
            }
            const materialSubTotal = Dinero(ticket.netValue)?.toUnit()
            const subTotal = materialSubTotal + feeOrPromotionAmount

            let isInLockedAccountingPeriodV2
            if (!enableLockAccountingV2) {
              isInLockedAccountingPeriodV2 = false
            } else if (!lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) {
              isInLockedAccountingPeriodV2 = false
            } else {
              isInLockedAccountingPeriodV2 = checkIfDateIsLocked(lockAccountingPeriod, ticket.effectiveDate)
            }
            if (isInLockedAccountingPeriodV2) {
              someTicketsAreLocked = true
            }

            const customerId = ticket.customer?.id

            if (!groupedTickets[customerId]) {
              groupedTickets[customerId] = []
            }
            groupedTickets[customerId].push({
              id: ticket.id,
              ticketId: ticket.ticketId,
              customerId: ticket.customer?.id,
              customerName: ticket.customer?.customerCommonIdentifierString,
              effectiveDate: dateFormatter(ticket.effectiveDate),
              orderNumber: ticket.transportationInfo?.purchaseOrderNumber || '-',
              status: TICKET_STATUSES[ticket.status?.value]?.name,
              netWeight: ticket.loadWeight?.net,
              value: subTotal,
              billId: ticket.bill?.id,
              billExternalId: ticket.bill?.externalId,
              locationId: ticket?.locationId,
            })
          })
        }

        setHasLockedTickets(someTicketsAreLocked)
        setGroupedTicketsData(groupedTickets)
      } catch (error) {
        setGroupedTicketsData({})
        dispatchSnackbar(dispatch, 'error', 'An error occurred while fetching the ticket data')
      }
      setPageIsReady(true)
    },
    [lockAccountingPeriod, enableLockAccountingV2],
  )

  const formatGroupedTicketsArray = () => {
    const formattedArray: any[] = []
    groupedTicketsArrayRef.current.forEach((ticketGroup: any[]) => {
      const highestTicket = ticketGroup.slice().sort((a, b) => {
        if (a.ticketId < b.ticketId) return 1
        if (a.ticketId > b.ticketId) return -1
        return 0
      })?.[0]
      formattedArray.push({
        billId: highestTicket.billId,
        inboundTicketIds: ticketGroup.map(ticket => ticket.id),
      })
    })
    return formattedArray
  }

  const formatData = data => ({
    startingCheckNumber: data.startingCheckNumber || '',
    paymentMethod: data.paymentMethod,
    currency: data.currency,
    paymentDate: data.paymentDate,
    completionDate: data.completionDate,
    bills: formatGroupedTicketsArray(),
    idempotencyKey: idempotencyKey?.current,
    notes: data.notes,
  })

  const onSave = async data => {
    try {
      const formattedData = formatData(data)
      let updatedBatchBill

      if (isUpdate) {
        const updateBatchBillResponse = await updateBatchBill({
          batchBillId,
          billIds: formattedData.bills?.map(bill => bill.id),
          batchBillRequestBody: formattedData,
        })

        if ('error' in updateBatchBillResponse) throw new Error()
        updatedBatchBill = updateBatchBillResponse?.data
      } else {
        const createBatchBillResponse = await createBatchBill({
          batchBillRequestBody: formattedData,
        })

        if ('error' in createBatchBillResponse) throw new Error()
        updatedBatchBill = createBatchBillResponse?.data
      }
      if (!isUpdate) {
        navigate(`/app/purchases/batch-bills/${updatedBatchBill?.id}`)
      } else if (updatedBatchBill?.status === BILL_STATUSES.PAID.id) {
        setPageIsSavingOrPaying(false)
        dispatchSnackbar(dispatch, 'success', 'Batch bill saved')
        setHasEditedInboundTickets(false)
      } else {
        setAfterSaveCallback(() => {
          return () => {
            dispatchSnackbar(dispatch, 'success', 'Batch bill saved')
            setHasEditedInboundTickets(false)
            setAfterSaveCallback(null)
          }
        })
      }
    } catch (error) {
      dispatchSnackbar(dispatch, 'error', `An error occurred while saving the batch bill`)
      setPageIsSavingOrPaying(false)
    }
    idempotencyKey.current = uuidv4()
  }

  const onPay = async () => {
    try {
      const payBatchBillResponse = await payBatchBill({
        batchBillId: batchBillData?.id || '',
        billIds: batchBillData?.bills?.map((bill: any) => bill.id),
        body: {
          idempotencyKey: idempotencyKey?.current,
        },
      })

      if ('error' in payBatchBillResponse) throw new Error()
      setAfterSaveCallback(() => {
        return () => {
          dispatchSnackbar(dispatch, 'success', 'Batch bill paid')
          setAfterSaveCallback(null)
        }
      })
    } catch (error) {
      dispatchSnackbar(dispatch, 'error', `An error occurred while paying the batch bill`)
      setPageIsSavingOrPaying(false)
    }
    idempotencyKey.current = uuidv4()
  }

  useEffect(() => {
    const newGroupedTicketsArray = Object.values(groupedTicketsData).sort((a, b) => {
      const customerNameA = a?.[0]?.customerName || ''
      const customerNameB = b?.[0]?.customerName || ''
      return customerNameA.localeCompare(customerNameB)
    })
    groupedTicketsArrayRef.current = newGroupedTicketsArray
    setGroupedTicketsArray(newGroupedTicketsArray)
  }, [groupedTicketsData])

  useEffect(() => {
    if (batchBillData) {
      const showCheckmark = batchBillData?.status === BILL_STATUSES.PAID.id
      contentContext.setContainerTitle(
        <Stack spacing={1} direction="row" alignItems="center">
          {isInLockedAccountingPeriodV2 && (
            <Tooltip title={generateLockTooltip('batch bill')} placement="top">
              <LockIcon color="info" />
            </Tooltip>
          )}
          <Typography variant="h5" data-testid="container-title">
            {`Batch Bill #${batchBillData?.externalId}`}
          </Typography>
          <Chip
            icon={showCheckmark ? <CheckCircleIcon /> : null}
            label={
              BILL_STATUSES[batchBillData?.status?.toUpperCase()]?.name ||
              startCase(toLower(batchBillData?.status || ''))
            }
            sx={{
              '.MuiChip-icon': {
                color: theme.palette.primary.main,
              },
            }}
          />
        </Stack>,
      )

      if (batchBillData?.status === BILL_STATUSES.SAVING.id) return

      reset({
        paymentMethod: batchBillData?.paymentMethod,
        startingCheckNumber: batchBillData?.startingCheckNumber,
        paymentDate: batchBillData?.paymentDate,
        completionDate: batchBillData?.completionDate,
        currency: batchBillData?.currency,
        notes: batchBillData?.notes,
      })

      setAggregatedData(previousAggregatedData => {
        const newAggregatedData = {
          ...previousAggregatedData,
        }
        batchBillData?.bills?.forEach(bill => {
          const customerId = bill?.customerId
          if (!customerId || !bill) return
          if (!newAggregatedData[customerId]) {
            newAggregatedData[customerId] = {
              billData: null,
              customerData: null,
            }
          }
          newAggregatedData[customerId].billData = bill
        })
        return newAggregatedData
      })

      const inboundTicketIds = batchBillData.bills?.flatMap(bill => bill.inboundTicketIds)
      fetchTickets(inboundTicketIds)

      const paymentIds = batchBillData.bills?.flatMap(bill => bill.paymentIds)
      fetchPayments(paymentIds)
    } else {
      contentContext.setContainerTitle('New Batch Bill')
    }
  }, [batchBillData])

  useEffect(() => {
    const totalValue = Object.values(billTotalValues).reduce(
      (aggregate: number, billTotalValue: number) =>
        Big(aggregate).plus(Big(billTotalValue)).round(ROUNDING_PRECISIONS.COST).toNumber(),
      0,
    )
    const remainingBalance = !pageIsReady
      ? 0
      : Big(totalValue)
          .minus(Big(batchBillData?.paymentsTotal || 0))
          .round(ROUNDING_PRECISIONS.COST)
          .toNumber()
    const balanceTextAction = {
      text: (
        <Stack spacing={0.5} direction="row" alignItems="end">
          <Typography sx={{ fontSize: '14px', color: theme.palette.text.primary }}>{'Balance: '}</Typography>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '16px',
              color: theme.palette.text.primary,
            }}
          >
            {`${pricePrepend}${setDecimalPrecision(remainingBalance, { decimalPrecisionOverride: 2 })}`}
          </Typography>
        </Stack>
      ),
      id: 'balance',
      disabled: true,
      variant: 'text',
      onClick: () => {},
      sx: { textTransform: 'none' },
    }

    const paymentAction = {
      text: 'PAY THESE BILLS',
      id: 'pay-batch-bill-button',
      icon: pageIsSavingOrPaying ? <CircularProgress style={{ width: '18px', height: '18px' }} /> : null,
      variant: 'contained',
      disabled: !pageIsReady || pageIsSavingOrPaying,
      onClick: handleSubmit(onPay),
    }

    const printChecksAction = {
      text: 'PRINT CHECKS',
      id: 'print-checks-button',
      variant: 'contained',
      disabled: !pageIsReady,
      onClick: () => setOpenPrintCheckBlade(true),
      type: ACTION_TYPE.TooltipButton,
      tooltipProps: {
        title: unprintedCheckPaymentsData.length === 0 ? 'All checks have already been printed' : '',
      },
    }

    const saveAction = {
      text: pageIsSavingOrPaying ? 'SAVING' : 'SAVE',
      id: 'save-batch-bill-button',
      icon: pageIsSavingOrPaying ? <CircularProgress style={{ width: '18px', height: '18px' }} /> : null,
      variant: 'contained',
      disabled: pageIsSavingOrPaying,
      onClick: handleSubmit(onSave),
    }

    const cancelAction = {
      text: 'CANCEL',
      id: 'bill-cancel-action-button',
      variant: 'text',
      onClick: () => {
        navigate(`/app/purchases/batch-bills/${batchBillId || ''}`)
      },
    }

    const actions: any[] = [balanceTextAction]

    if ((pageIsSavingOrPaying && !pageIsReady) || !isUpdate || isDirty || hasEditedInboundTickets) {
      actions.push(saveAction)
      actions.push(cancelAction)
    } else if (!batchBillIsPaid && groupedTicketsArray.length > 0) {
      actions.push(paymentAction)
    } else if (
      batchBillIsPaid &&
      batchBillData?.paymentMethod === PAYMENT_METHODS_VALUES.CHECK.toLowerCase() &&
      unprintedCheckPaymentsData.length === groupedTicketsArray.length
    ) {
      /*
        NOTE:
        The printChecksAction is hidden if a user prints any of the single checks individually. The reason why is because the
        PDF check endpoint for batch bills will currently print all checks, regardless of whether they have already been printed
        yet or not. There is currently no way to print only all of the UNPRINTED batch bill checks.

        If we update the PDF check endpoint for batch bills to only print the unprinted batch bill checks, then we can remove
        the unprintedCheckPaymentsData.length === groupedTicketsArray.length validation
      */
      actions.push(printChecksAction)
    }

    contentContext.setContainerActions(actions)
  }, [
    isUpdate,
    pageIsReady,
    pageIsSavingOrPaying,
    batchBillIsPaid,
    isDirty,
    hasEditedInboundTickets,
    batchBillData,
    billTotalValues,
    unprintedCheckPaymentsData,
    groupedTicketsArray,
  ])

  const currentPaymentMethod = watch('paymentMethod')

  const renderBillsSection = () => {
    if (!pageIsReady || isLoadingInboundTicketsByIds) {
      return null
    }
    if (groupedTicketsArray?.length === 0) {
      return <Grid alignSelf="center">No tickets have been added</Grid>
    }
    return groupedTicketsArray?.map((tickets: any[], index: number) => {
      const customerId = tickets?.[0]?.customerId
      const billData = aggregatedData[customerId]?.billData
      const paymentData: any = billData ? paymentsData?.find((payment: any) => payment.billId === billData.id) : null
      const showPrintCheckButton =
        batchBillIsPaid &&
        batchBillData?.paymentMethod === PAYMENT_METHODS_VALUES.CHECK.toLowerCase() &&
        !paymentData?.accountId

      return (
        <React.Fragment key={customerId}>
          <BatchBillTicketTable
            batchBillData={batchBillData}
            tickets={tickets}
            setGroupedTicketsData={setGroupedTicketsData}
            hasEditedInboundTickets={hasEditedInboundTickets}
            setHasEditedInboundTickets={setHasEditedInboundTickets}
            paymentMade={batchBillIsPaid}
            pricePrepend={pricePrepend}
            setBillTotalValues={setBillTotalValues}
            showPrintCheckButton={showPrintCheckButton}
            billData={billData}
            paymentData={paymentData}
            setAggregatedData={setAggregatedData}
            pdfPrint={pdfPrint}
          />
        </React.Fragment>
      )
    })
  }

  return (
    <FormProvider {...methods}>
      <form>
        <Stack minHeight="calc(100vh - 244px)">
          <Stack direction={{ xs: 'column', md: 'row' }} p={3} spacing={3}>
            <SubFormLayout
              formName="Payment Information"
              boxStyles={{ width: '100%' }}
              isLoading={showSkeletonLoaders}
              fields={[
                [
                  {
                    name: 'paymentMethod',
                    label: 'Payment Method',
                    fieldType: 'dropdown',
                    dropdownOptions: PAYMENT_METHOD_OPTIONS,
                    defaultValue: yardPreferredPaymentMethod,
                    InputLabelProps: { shrink: true },
                    disabled: batchBillIsPaid,
                  },
                  {
                    name: 'startingCheckNumber',
                    label: 'Starting Check Number',
                    fieldType: 'input',
                    hidden: currentPaymentMethod !== PAYMENT_METHODS_VALUES.CHECK.toLowerCase(),
                    disabled: batchBillIsPaid,
                  },
                  {
                    name: 'paymentDate',
                    label: 'Payment Date',
                    fieldType: 'date',
                    defaultDate: new Date(),
                    datePickerProps: {
                      disabled: batchBillIsPaid,
                      minDate:
                        lockAccountingPeriod?.isLockedForAccounting && lockAccountingPeriod?.lockAccountingDate
                          ? addToDate(new Date(lockAccountingPeriod.lockAccountingDate))
                          : undefined,
                    },
                    InputLabelProps: { shrink: true },
                    disabled: batchBillIsPaid,
                  },
                ],
              ]}
            />
            <SubFormLayout
              formName="Batch Details"
              boxStyles={{ width: '100%' }}
              isLoading={showSkeletonLoaders}
              fields={[
                [
                  {
                    name: 'completionDate',
                    label: 'Completion Date',
                    fieldType: 'date',
                    defaultDate: new Date(),
                    datePickerProps: {
                      disabled: batchBillIsPaid,
                      minDate:
                        lockAccountingPeriod?.isLockedForAccounting && lockAccountingPeriod?.lockAccountingDate
                          ? addToDate(new Date(lockAccountingPeriod.lockAccountingDate))
                          : undefined,
                    },
                    InputLabelProps: { shrink: true },
                    disabled: batchBillIsPaid,
                  },
                  {
                    name: 'currency',
                    label: 'Currency',
                    fieldType: 'dropdown',
                    dropdownOptions: CURRENCY_OPTIONS,
                    defaultValue: defaultCurrency,
                    InputLabelProps: { shrink: true },
                    disabled: batchBillIsPaid || groupedTicketsArray?.length > 0,
                  },
                ],
              ]}
            />
          </Stack>
          <Stack p={3}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="h6" color="textSecondary">
                Bills
              </Typography>
              {batchBillIsPaid ? null : (
                <Button
                  variant="outlined"
                  data-testid="add-tickets-button"
                  onClick={() => {
                    setOpenAddModal(true)
                  }}
                >
                  {`${groupedTicketsArray?.length > 0 ? 'ADD/REMOVE' : 'ADD'} TICKETS`}
                </Button>
              )}
            </Stack>
            {renderBillsSection()}
          </Stack>
        </Stack>
        <BatchBillFooter
          billTotalValues={billTotalValues}
          pricePrepend={pricePrepend}
          isLoading={showSkeletonLoaders}
        />
      </form>
      {openAddModal && (
        <InboundTicketsModal
          title={`Add Tickets`}
          buttonTitle="Save Selection"
          selectedList={groupedTicketsArray?.flatMap((groupedTickets: any[]) =>
            groupedTickets.map(ticket => ticket.id),
          )}
          open={openAddModal}
          onAdd={ticketIds => {
            setOpenAddModal(false)
            setHasEditedInboundTickets(true)
            fetchTickets(ticketIds)
          }}
          onClose={() => setOpenAddModal(false)}
          billIds={billIds}
          currentCustomer={null}
          getPaymentTerms={() => {}}
          currentCurrency={currency}
          isBatchBill
        />
      )}
      <PrintCheckBlade
        open={openPrintCheckBlade}
        setOpen={setOpenPrintCheckBlade}
        checkPaymentsData={unprintedCheckPaymentsData}
        selectedTicketsData={selectedTicketsDataArray}
        customersData={customersDataArray}
        handleSubmit={checkPrintHandleSubmit}
        setValue={checkPrintSetValue}
        watch={checkPrintWatch}
        trigger={checkPrintTrigger}
        control={checkPrintControl}
        reset={checkPrintReset}
        errors={checkPrintErrors}
        isValid={checkPrintIsValid}
        onPrint={async () => {
          await onPrintCheck()
          setOpenPrintCheckBlade(false)
          pdfPrint(
            {},
            `/core-api/financial/batch-bill/${batchBillId}/pdf/${PDF_TYPE.CHECK.toLowerCase()}`,
            '',
            PDF_TYPE.CHECK,
          )
        }}
        pricePrepend={pricePrepend}
        isBatchBill
        startingCheckNumberOverrides={startingCheckNumberOverrides}
      />
    </FormProvider>
  )
}

export default BatchBillContainer
